/**
 * React Query hooks for app operations.
 * Apps are sandboxes with the type=app label.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useApps
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { appService } from '@/services/appService';
import { showErrorToast } from '@/utils/errorToast';
import type {
  SandboxRequest,
  SandboxResponse,
  SnapshotRequest,
  AppAddPortRequest,
  RemovePortRequest,
  UpdatePortsRequest,
  EgressPolicy,
} from '@/types';

/**
 * Query key factory for apps.
 * Ensures consistent cache key generation.
 */
export const appKeys = {
  all: ['apps'] as const,
  lists: () => [...appKeys.all, 'list'] as const,
  list: () => [...appKeys.lists()] as const,
  details: () => [...appKeys.all, 'detail'] as const,
  detail: (id: string) => [...appKeys.details(), id] as const,
};

/**
 * Hook to fetch total count of apps
 *
 * @example
 * const { data: count } = useAppsCount();
 */
export function useAppsCount() {
  return useQuery({
    queryKey: [...appKeys.all, 'count'],
    queryFn: () => appService.getCount(),
    staleTime: 30 * 1000, // Cache for 30 seconds
  });
}

/**
 * Hook to fetch apps with pagination
 *
 * @param page - Page number (1-based)
 * @param pageSize - Number of items per page
 * @example
 * const { data, isLoading, error } = useApps(1, 25);
 */
export function useApps(page: number = 1, pageSize: number = 25) {
  console.log('[useApps] Hook called - Page:', page, 'PageSize:', pageSize);
  return useQuery({
    queryKey: [...appKeys.list(), page, pageSize],
    queryFn: async () => {
      console.log('[useApps] queryFn executing...');
      const result = await appService.list(page, pageSize);
      console.log('[useApps] queryFn completed, result:', result);
      return result;
    },
  });
}

/**
 * Hook to fetch a single app by ID
 *
 * @param id - The app ID
 * @example
 * const { data, isLoading, error } = useApp('app-123');
 */
export function useApp(id: string) {
  return useQuery({
    queryKey: appKeys.detail(id),
    queryFn: () => appService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch egress decisions for an app
 *
 * @param id - The app ID
 * @example
 * const { data, isLoading, error } = useAppEgressDecisions('app-123');
 */
export function useAppEgressDecisions(id: string) {
  return useQuery({
    queryKey: [...appKeys.detail(id), 'egress-decisions'],
    queryFn: () => appService.getEgressDecisions(id),
    enabled: !!id,
    staleTime: 10 * 1000, // Cache for 10 seconds
    refetchInterval: 30 * 1000, // Auto-refetch every 30 seconds
  });
}

/**
 * Hook to set the egress policy for an app
 */
export function useSetAppEgressPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, policy }: { appId: string; policy: EgressPolicy }) =>
      appService.setEgressPolicy(appId, policy),
    onSuccess: (_, { appId }) => {
      // Invalidate the app detail to refetch with updated policy
      queryClient.invalidateQueries({ queryKey: appKeys.detail(appId) });
      toast.success('Egress policy updated successfully');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to update egress policy: ${error.message}`);
    },
  });
}

/**
 * Hook to create a new app
 *
 * @example
 * const { mutate, isPending } = useCreateApp();
 * mutate(appData, { onSuccess: () => navigate('/apps') });
 */
export function useCreateApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SandboxRequest) => appService.create(request),
    onSuccess: (data: SandboxResponse) => {
      // Invalidate and refetch apps list
      queryClient.invalidateQueries({ queryKey: appKeys.lists() });
      // Pre-populate the detail cache
      queryClient.setQueryData(appKeys.detail(data.id), data);
      toast.success(`App "${data.id}" created successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create app: ${error.message}`);
    },
  });
}

/**
 * Hook to delete an app
 *
 * @example
 * const { mutate, isPending } = useDeleteApp();
 * mutate('app-123');
 */
export function useDeleteApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appService.delete(id),
    onSuccess: (_, id) => {
      // Invalidate the list cache
      queryClient.invalidateQueries({ queryKey: appKeys.lists() });
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: appKeys.detail(id) });
      toast.success(`App "${id}" deleted successfully`);
    },
    onError: (error: Error, id) => {
      showErrorToast(`Failed to delete app "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to resume a stopped app
 *
 * @example
 * const { mutate, isPending } = useResumeApp();
 * mutate('app-123');
 */
export function useResumeApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appService.resume(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: appKeys.lists() });
      toast.success(`App "${id}" resumed successfully`);
    },
    onError: (error: Error, id) => {
      showErrorToast(`Failed to resume app "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to stop an app
 *
 * @example
 * const { mutate, isPending } = useStopApp();
 * mutate('app-123');
 */
export function useStopApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appService.stop(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: appKeys.lists() });
      toast.success(`App "${id}" stopped successfully`);
    },
    onError: (error: Error, id) => {
      showErrorToast(`Failed to stop app "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to create a snapshot of an app
 *
 * @example
 * const { mutate, isPending } = useCreateAppSnapshot();
 * mutate({ id: 'app-123', labels: {} });
 */
export function useCreateAppSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, labels }: { id: string; labels?: SnapshotRequest['labels'] }) =>
      appService.createSnapshot(id, { labels }),
    onSuccess: (data, { id }) => {
      // Invalidate snapshots list to show the new snapshot
      queryClient.invalidateQueries({ queryKey: ['snapshots'] });
      toast.success(`Snapshot "${data.snapshotId}" created successfully from app "${id}"`);
    },
    onError: (error: Error, { id }) => {
      showErrorToast(`Failed to create snapshot for app "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to execute a command in an app
 *
 * @example
 * const { mutateAsync, isPending } = useAppExecuteCommand();
 * const result = await mutateAsync({ appId: 'app-123', command: 'ls -la' });
 */
export function useAppExecuteCommand() {
  return useMutation({
    mutationFn: ({
      appId,
      command,
      workingDirectory,
    }: {
      appId: string;
      command: string;
      workingDirectory?: string;
    }) => appService.executeCommand(appId, command, workingDirectory),
    onError: (error: Error) => {
      showErrorToast(`Command execution failed: ${error.message}`);
    },
  });
}

/**
 * Hook to add a port to an app (with app-specific options like activationMode and auth)
 *
 * @example
 * const { mutate, isPending } = useAddAppPort();
 * mutate({ appId: 'app-123', port: 8080, name: 'http', activationMode: 'OnDemand', auth: { github: { allowedEmails: ['user@example.com'] } } });
 */
export function useAddAppPort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, ...request }: { appId: string } & AppAddPortRequest) =>
      appService.addPort(appId, request),
    onSuccess: (data, { appId }) => {
      queryClient.invalidateQueries({ queryKey: appKeys.detail(appId) });
      toast.success(`Port ${data.port} added successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to add port: ${error.message}`);
    },
  });
}

/**
 * Hook to remove a port from an app
 *
 * @example
 * const { mutate, isPending } = useRemoveAppPort();
 * mutate({ appId: 'app-123', port: 8080 });
 */
export function useRemoveAppPort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, ...request }: { appId: string } & RemovePortRequest) =>
      appService.removePort(appId, request),
    onSuccess: (_, { appId, port, name }) => {
      queryClient.invalidateQueries({ queryKey: appKeys.detail(appId) });
      const portIdentifier = name || port;
      toast.success(`Port ${portIdentifier} removed successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to remove port: ${error.message}`);
    },
  });
}

/**
 * Hook to update all ports on an app
 *
 * @example
 * const { mutate, isPending } = useUpdateAppPorts();
 * mutate({ appId: 'app-123', ports: [{ port: 8080 }, { port: 3000, name: 'api' }] });
 */
export function useUpdateAppPorts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, ...request }: { appId: string } & UpdatePortsRequest) =>
      appService.updatePorts(appId, request),
    onSuccess: (data, { appId }) => {
      queryClient.invalidateQueries({ queryKey: appKeys.detail(appId) });
      toast.success(`Ports updated successfully (${data.length} ports)`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to update ports: ${error.message}`);
    },
  });
}
